/**
 * El `?ref` del cliente, guardado en el carrier del alta en curso
 * (`lib/pending-signup.ts`), que sobrevive al round-trip same-origin del OAuth:
 * el `?ref` capturado en la landing sigue disponible cuando el usuario vuelve
 * tras autenticarse, y se adjunta al único fetch de sync
 * (contexts/auth-account-sync.ts).
 *
 * `referredByCode` es entrada NO confiable (viene del link o del pegado manual
 * del nativo, DD7/ADR-013): aquí sólo se acota (no vacío, ≤64) para no persistir
 * basura; la validación real (fail-closed contra el referralCode del referidor)
 * vive en el servidor, en el sellado.
 */
import {
  forgetReferralCode,
  isPlausibleReferralCode,
  readPendingSignup,
  rememberReferralCode
} from '@/lib/pending-signup'

export const storeReferralCode = rememberReferralCode
export const clearReferralCode = forgetReferralCode

export function readReferralCode(): string | null {
  return readPendingSignup().referredByCode ?? null
}

function parseReferralCodeFromSearch(search: string): string | null {
  try {
    // URLSearchParams.get devuelve el PRIMER valor si `ref` aparece repetido.
    const code = new URLSearchParams(search).get('ref')
    return code && isPlausibleReferralCode(code) ? code : null
  } catch {
    return null
  }
}

/**
 * Captura el `?ref` de la URL (por defecto, la de la landing actual) y lo
 * persiste en el carrier. Absorbe con gracia los casos ausente/vacío/malformado
 * (nunca lanza) — sólo almacena un código plausible.
 */
export function captureReferralCodeFromUrl(
  search: string = typeof window === 'undefined' ? '' : window.location.search
): void {
  const code = parseReferralCodeFromSearch(search)
  if (code) {
    storeReferralCode(code)
  }
}
